import { useState, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useThemeStore } from '../store/themeStore';
import { useWebSocket } from '../hooks/useWebSocket';
import { Bars3Icon, ChevronDownIcon, SunIcon, MoonIcon } from '@heroicons/react/24/outline';
import Sidebar from './Sidebar';

function Layout() {
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const theme = useThemeStore((state) => state.theme);
  const toggleTheme = useThemeStore((state) => state.toggleTheme);
  const navigate = useNavigate();
  const { connectionStatus, isConnected, lastMessage, toggleConnection } = useWebSocket(null, true);
  
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  
  // WebSocket messages are handled by specific consumers
  // Keeping this effect for future message handling if needed
  useEffect(() => {
    if (lastMessage) {
      // Handle WebSocket messages here if needed
    }
  }, [lastMessage]);
  
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleMobileSidebar = () => {
    setMobileSidebarOpen(!mobileSidebarOpen);
  };

  const toggleUserMenu = () => {
    setUserMenuOpen(!userMenuOpen);
  };

  // Get status display properties
  const getStatusProps = (status) => {
    switch (status) {
      case 'connected':
        return { color: 'bg-green-500', text: 'Connected', hoverText: 'WebSocket connected and authenticated' };
      case 'authenticating':
        return { color: 'bg-yellow-500', text: 'Authenticating', hoverText: 'Validating authentication token' };
      case 'error':
        return { color: 'bg-red-500', text: 'Error', hoverText: 'Connection or authentication failed' };
      case 'offline':
      default:
        return { color: 'bg-gray-400', text: 'Offline', hoverText: 'Click to connect' };
    }
  };

  const statusProps = getStatusProps(connectionStatus);
  
  return (
    <div className="h-screen overflow-hidden bg-gradient-to-br from-gray-900 via-obn-blue to-gray-800">
      <Sidebar
        isCollapsed={sidebarCollapsed}
        setIsCollapsed={setSidebarCollapsed}
        isMobileOpen={mobileSidebarOpen}
        setIsMobileOpen={setMobileSidebarOpen}
      />

      {/* Main content */}
      <div className={`
        flex flex-col h-screen transition-all duration-300 ease-in-out
        ${sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'}
      `}>
        {/* Top bar */}
        <div className="flex h-16 shrink-0 items-center gap-x-4 border-b border-white/10 px-4 sm:gap-x-6 sm:px-6 lg:px-8">
          {/* Mobile menu button */}
          <button
            type="button"
            className="-m-2.5 p-2.5 text-white/80 hover:text-white lg:hidden"
            onClick={toggleMobileSidebar}
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>

          {/* Separator */}
          <div className="h-6 w-px bg-white/20 lg:hidden" aria-hidden="true" />

          <div className="flex flex-1 justify-end gap-x-4 self-stretch lg:gap-x-6">
            {/* WebSocket status */}
            <div className="flex items-center">
              <button
                onClick={toggleConnection}
                title={statusProps.hoverText}
                className="flex items-center space-x-2 px-3 py-1.5 rounded-md border border-white/20 hover:bg-white/10 transition-colors text-sm"
              >
                <div className={`w-2 h-2 rounded-full ${statusProps.color} ${connectionStatus === 'authenticating' ? 'animate-pulse' : ''}`} />
                <span className="text-white/80 hidden sm:inline">
                  {statusProps.text}
                </span>
              </button>
            </div>

            {/* Theme toggle */}
            <div className="flex items-center">
              <button
                onClick={toggleTheme}
                title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                className="p-1.5 rounded-md border border-white/20 hover:bg-white/10 transition-colors text-white/80"
              >
                <span className="sr-only">Toggle theme</span>
                {theme === 'dark' ? (
                  <SunIcon className="h-5 w-5" aria-hidden="true" />
                ) : (
                  <MoonIcon className="h-5 w-5" aria-hidden="true" />
                )}
              </button>
            </div>

            {/* Profile dropdown */}
            <div className="relative flex items-center">
              <button
                type="button"
                className="-m-1.5 flex items-center p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                onClick={toggleUserMenu}
              >
                <span className="sr-only">Open user menu</span>
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-obn-blue ring-1 ring-white/20 rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-white">
                      {(user?.name || user?.email || '?').charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className="hidden lg:flex lg:items-center">
                    <span className="text-sm font-semibold text-white" aria-hidden="true">
                      {user?.name || user?.email}
                    </span>
                    <ChevronDownIcon className="ml-2 h-4 w-4 text-white/60" aria-hidden="true" />
                  </span>
                </div>
              </button>
              
              {/* User menu dropdown */}
              {userMenuOpen && (
                <>
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setUserMenuOpen(false)}
                  ></div>
                  <div className="absolute right-0 z-20 mt-2.5 w-48 origin-top-right rounded-md bg-gray-800 py-2 shadow-lg ring-1 ring-white/10">
                    <button
                      onClick={() => {
                        handleLogout();
                        setUserMenuOpen(false);
                      }}
                      className="block w-full px-3 py-1 text-left text-sm leading-6 text-gray-100 hover:bg-gray-700"
                    >
                      Sign out
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto overscroll-y-none py-6 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
          <div className="px-0 sm:px-6 lg:px-8 max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

export default Layout;